'use client'

import React, { createContext, useContext, ReactNode } from 'react'

/**
 * Session Context
 *
 * Provides session and authentication data to client components without prop drilling.
 * Populated from server-side data in RootLayout.
 *
 * - user: Fresh from DB (getCachedUser) when authenticated, so logo, username, etc. stay
 *   up to date; profile changes reflect in nav and all useSession() consumers after refresh.
 * - hasAdminRole: Whether the user has admin access (GOD or assigned org/fed/comp admin); used by nav to show Admin link.
 * - token was removed; auth uses NextAuth session/credentials.
 * - For translations, use TranslationProvider (consumes lang from here, provides t() via useTranslation).
 */
export interface AdministrationTools {
    users: boolean
    designer: boolean
    settings: boolean
    billing: boolean
}

const hiddenAdministrationTools: AdministrationTools = {
    users: false,
    designer: false,
    settings: false,
    billing: false
}

interface SessionContextValue {
    lang: string
    user: IUser | null
    hasAdminRole: boolean
    administrationTools: AdministrationTools
}

const SessionContext = createContext<SessionContextValue>({
    lang: 'en',
    user: null,
    hasAdminRole: false,
    administrationTools: hiddenAdministrationTools
})

export const useSession = () => useContext(SessionContext)

interface SessionProviderProps {
    children: ReactNode
    lang: string
    user: IUser | null
    /** Whether the current user has admin access. Default false. */
    hasAdminRole?: boolean
    administrationTools?: AdministrationTools
}

/**
 * SessionProvider - Client Component
 *
 * Provides session data to client components via React Context.
 *
 * Architecture:
 * - Receives server-side data (lang, user) from RootLayout
 * - Auth uses NextAuth session / credentials; token (old API) is no longer passed
 * - Client components consume via useSession() hook
 * - For translations, use TranslationProvider via useTranslation() hook
 */
export function SessionProvider({
    children,
    lang,
    user,
    hasAdminRole = false,
    administrationTools = hiddenAdministrationTools
}: SessionProviderProps): React.JSX.Element {
    return (
        <SessionContext.Provider value={{ lang, user, hasAdminRole, administrationTools }}>
            {children}
        </SessionContext.Provider>
    )
}
